import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import {
    Button,
    createTheme,
    FormControl,
    Grid,
    InputLabel,
    Link,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    outlinedInputClasses,
    Paper,
    Select,
    ThemeProvider,
    Typography,
    useTheme
} from "@mui/material";
import getLocale, { getUiDirection } from "../helper/Locale";
import { getApiPath } from "../helper/runtimeConfig";
import '../style/TrackForm.css';
import CustomTextField from "./CustomTextField";
import countriesData from "../i18n/countries.json"

export const customTextFieldTheme = (outerTheme, borderColor = '#E0E3E7', hoverColor = '#B2BAC2', focusedColor = '#6F7E8C') =>
    createTheme({
        palette: {
            mode: outerTheme.palette.mode,
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '--TextField-brandBorderColor': borderColor,
                        '--TextField-brandBorderHoverColor': hoverColor,
                        '--TextField-brandBorderFocusedColor': focusedColor,
                        backgroundColor: "rgb(232, 241, 250)",
                        borderTopRightRadius: "5px",
                        borderTopLeftRadius: "5px",
                        '& label.Mui-focused': {
                            color: 'var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: 'var(--TextField-brandBorderHoverColor)',
                        },
                        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: 'var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: "rgb(232, 241, 250)",
                        [`&.Mui-focused, &:hover, &:active`]: {
                            backgroundColor: "rgb(232, 241, 250)"
                        },
                        '&:before, &:after': {
                            borderBottom: '2px solid var(--TextField-brandBorderColor)',
                        },
                        '&:hover:not(.Mui-disabled, .Mui-error):before': {
                            borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
                        },
                        '&.Mui-focused:after': {
                            borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    root: {
                        '&:before': {
                            borderBottom: '2px solid var(--TextField-brandBorderColor)',
                        },
                        '&:hover:not(.Mui-disabled, .Mui-error):before': {
                            borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
                        },
                        '&.Mui-focused:after': {
                            borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
        },
    });

const SEARCH_MODES = ['invoice_number', 'tracking_number', 'phone_number'];
const INPUT_LABEL_KEYS = {
    invoice_number: 'track_input_by_invoice_number',
    tracking_number: 'track_input_by_tracking_number',
    phone_number: 'track_input_by_phone_number',
};
const OUTCOME_MESSAGE_KEYS = {
    not_found: 'no_tracking_links_found',
    error: 'invalid_search_value',
    verification_failed: 'verification_failed',
    too_many_attempts: 'too_many_attempts',
    captcha_failed: 'captcha_failed',
};
const CAPTCHA_ERROR_CODE = 100007;
const RECAPTCHA_READY_POLL_MS = 100;
export const RECAPTCHA_ACTION = 'tracking_search';
export const RECAPTCHA_READY_WAIT_MS = 5000;
export const RECAPTCHA_TIMEOUT_MS = 10000;
export const SEARCH_TIMEOUT_MS = 20000;

export const sanitizeSearchBy = (by) => (SEARCH_MODES.includes(by) ? by : 'tracking_number');
export const sanitizeCountryCode = (cc) => {
    const upper = (cc ?? '').toUpperCase();
    return Object.prototype.hasOwnProperty.call(countriesData, upper) ? upper : 'US';
};
// Mirrors WidgetTrait::_validateSearchInput phone branch: strip to [+0-9], then /^\+?[0-9]{7,15}$/.
export const isPhoneShapeValid = (value) => /^\+?[0-9]{7,15}$/.test((value ?? '').replace(/[^+0-9]/g, ''));

export const failureOutcome = (isInvoiceSearch, httpStatus, body) => {
    if (httpStatus === 429) return 'too_many_attempts';
    if (httpStatus === 403 && Number(body?.data?.error_code) === CAPTCHA_ERROR_CODE) return 'captcha_failed';
    if (isInvoiceSearch) return 'verification_failed';
    return httpStatus === 404 ? 'not_found' : 'error';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Inside the provider executeRecaptcha stays undefined until Google's script is ready;
// without a provider it is a function that throws synchronously.
export const getRecaptchaToken = async (executeRecaptchaRef) => {
    const readyBy = Date.now() + RECAPTCHA_READY_WAIT_MS;
    while (typeof executeRecaptchaRef.current !== 'function' && Date.now() < readyBy) {
        await sleep(RECAPTCHA_READY_POLL_MS);
    }
    const executeRecaptcha = executeRecaptchaRef.current;
    if (typeof executeRecaptcha !== 'function') return '';
    let timer;
    try {
        const timeout = new Promise((resolve) => { timer = setTimeout(resolve, RECAPTCHA_TIMEOUT_MS, ''); });
        const token = await Promise.race([executeRecaptcha(RECAPTCHA_ACTION), timeout]);
        return typeof token === 'string' ? token : '';
    } catch (e) {
        return '';
    } finally {
        clearTimeout(timer);
    }
};

// Resolves to the parsed body, or null when the request or its body has not settled by the deadline.
const postSearch = async (apiPath, init, onStatus) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => {
            if (controller) controller.abort();
            resolve(null);
        }, SEARCH_TIMEOUT_MS);
    });
    const exchange = (async () => {
        const response = await fetch(apiPath, controller ? { ...init, signal: controller.signal } : init);
        onStatus(response.status);
        return response.json();
    })();
    exchange.catch(() => {});
    try {
        return await Promise.race([exchange, timeout]);
    } finally {
        clearTimeout(timer);
    }
};

const TrackForm = () => {
    const outerTheme = useTheme();
    const { t } = useTranslation();
    const { theme } = useSelector((store) => store.theme);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const executeRecaptchaRef = useRef(executeRecaptcha);
    const inFlight = useRef(false);

    const uiDirection = getUiDirection();
    const params = new URLSearchParams(window.location.search);
    const orgParam = params.get('org') ?? [];
    const locParam = params.get('loc') ?? [];
    const autoOpen = params.get('ao') ? params.get('ao') === 'true' : true;
    const isOrgSet = (Array.isArray(orgParam) && orgParam.length > 0) || (typeof orgParam === 'string' && orgParam.trim().length > 0);

    const [searchBy, setSearchBy] = useState(() => sanitizeSearchBy(params.get('by') ?? 'invoice_number'));
    const [countryCode, setCountryCode] = useState(() => sanitizeCountryCode(params.get('cc')));
    const [inputValue, setInputValue] = useState('');
    const [verifyPhone, setVerifyPhone] = useState('');
    const [step, setStep] = useState('search');
    const [loading, setLoading] = useState(false);
    const [trackingData, setTrackingData] = useState(null);
    const [outcome, setOutcome] = useState(null);

    const needsPhoneVerification = searchBy === 'invoice_number';

    useEffect(() => {
        executeRecaptchaRef.current = executeRecaptcha;
    }, [executeRecaptcha]);

    useEffect(() => {
        if (Array.isArray(trackingData) && trackingData.length === 1 && autoOpen) {
            const trackingUrl = trackingData[0].url;
            const didWindowOpen = window.open(trackingUrl, '_blank');
            if (!didWindowOpen) {
                const a = document.createElement('a');
                a.target = '_blank';
                a.href = trackingUrl;
                a.click();
            }
        }
    }, [trackingData, autoOpen]);

    const resetForm = () => {
        setInputValue('');
        setVerifyPhone('');
        setStep('search');
        setTrackingData(null);
        setOutcome(null);
    };

    const runSearch = async () => {
        if (inFlight.current) return;
        const isInvoiceSearch = needsPhoneVerification;
        const apiPath = getApiPath();
        if (!apiPath) {
            setOutcome(failureOutcome(isInvoiceSearch, null, null));
            return;
        }
        inFlight.current = true;
        setLoading(true);
        const body = {
            search: inputValue,
            by: searchBy,
            country_code: countryCode,
            org_id: typeof orgParam === 'string' ? orgParam.split(',') : orgParam,
            loc_id: typeof locParam === 'string' ? locParam.split(',') : locParam,
        };
        if (isInvoiceSearch) {
            body.phone_number = verifyPhone;
        }
        let httpStatus = null;
        try {
            body.recaptcha_token = await getRecaptchaToken(executeRecaptchaRef);
            const data = await postSearch(apiPath, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Language': getLocale() },
                body: JSON.stringify(body),
            }, (status) => { httpStatus = status; });
            const rows = data && data.data && data.data.tracking_data;
            if (httpStatus === 200 && Array.isArray(rows) && rows.length > 0) {
                setTrackingData(rows);
                return;
            }
            setOutcome(failureOutcome(isInvoiceSearch, httpStatus, data));
        } catch (e) {
            setOutcome(failureOutcome(isInvoiceSearch, httpStatus, null));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    const canSubmitSearchStep = isOrgSet && inputValue.trim() !== '' && !loading;
    const canSubmitVerifyStep = isOrgSet && isPhoneShapeValid(verifyPhone) && !loading;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (step === 'search') {
            if (!canSubmitSearchStep) return;
            if (needsPhoneVerification) {
                setStep('verify');
                return;
            }
            runSearch();
        } else if (canSubmitVerifyStep) {
            runSearch();
        }
    };

    const handleChangeSearchBy = (event) => {
        setSearchBy(sanitizeSearchBy(event.target.value));
        setInputValue('');
        setVerifyPhone('');
    };

    const buttonStyle = { backgroundColor: theme.buttonBackgroundColor, color: theme.buttonTextColor };
    const forwardIcon = uiDirection === 'rtl' ? <ArrowBackIcon style={{ marginInlineStart: 5 }} /> : <ArrowForwardIcon style={{ marginInlineStart: 5 }} />;
    const backIcon = uiDirection === 'rtl' ? <ArrowForwardIcon style={{ marginInlineEnd: 5 }} /> : <ArrowBackIcon style={{ marginInlineEnd: 5 }} />;

    const countrySelect = (
        <FormControl fullWidth>
            <InputLabel id="phone-country-code-label">{t("country_code")}</InputLabel>
            <Select
                labelId="phone-country-code-label"
                id="country-code-select"
                sx={{ backgroundColor: "white", borderColor: "#333", borderWidth: "1px" }}
                label={t("country_code")}
                size="small"
                value={countryCode}
                disabled={!isOrgSet || loading}
                onChange={(e) => setCountryCode(e.target.value)}
            >
                {Object.entries(countriesData).map(([code, details]) => (
                    <MenuItem key={code} value={code}>{code} (<bdi dir="ltr">{details.dial_code}</bdi>)</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const outcomeScreen = (messageKey) => (
        <Grid container>
            <Grid item xs={12}>
                <Paper id="not-found-text" elevation={0} role="alert"
                       style={{ backgroundColor: theme.backgroundColor, color: theme.primaryTextColor }}>
                    {t(messageKey)}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Button fullWidth id="not-found-btn" variant="contained" onClick={resetForm} size="medium" style={buttonStyle}>
                    {t("start_over")} <ReplayOutlinedIcon/>
                </Button>
            </Grid>
        </Grid>
    );

    if (Array.isArray(trackingData) && trackingData.length >= 1) {
        return (
            <Grid container>
                <Grid item xs={12}>
                    <List className="result-list">
                        {trackingData.map((item, index) => (
                            <ListItem key={index} className="result-list-item" style={{ borderColor: theme.buttonBackgroundColor }}>
                                <Link href={item.url} target="_blank" style={{ textDecoration: 'none', width: '100%' }}>
                                    <ListItemText
                                        style={{ color: theme.primaryTextColor, width: '100%', textAlign: 'center', margin: 'unset' }}
                                        primary={item.type}
                                        secondary={<Typography sx={{ display: 'inline' }} component="span" variant="body2" color={theme.secondaryTextColor}>{item.date}</Typography>}
                                    />
                                </Link>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
                <Grid item xs={12}>
                    <Button id="search-again-btn" variant="contained" onClick={resetForm} size="medium" style={buttonStyle}>
                        {t("start_over")} <ReplayOutlinedIcon/>
                    </Button>
                </Grid>
            </Grid>
        );
    }
    if (outcome) return outcomeScreen(OUTCOME_MESSAGE_KEYS[outcome]);

    return (
        <ThemeProvider theme={customTextFieldTheme(outerTheme)}>
            <form id="track-form" onSubmit={handleSubmit} noValidate>
                {step === 'search' ? (
                    <>
                        <Grid container spacing="5px">
                            <Grid item xs={12} sm={4} md={3} lg={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="search-by-select-label">{t("search_by")}</InputLabel>
                                    <Select labelId="search-by-select-label" id="search-by-select"
                                            sx={{ backgroundColor: "white", borderColor: "#333", borderWidth: "1px" }}
                                            size="small" label={t("search_by")} value={searchBy}
                                            disabled={!isOrgSet || loading} onChange={handleChangeSearchBy}>
                                        <MenuItem value="invoice_number" dir={uiDirection}>{t("invoice_number")}</MenuItem>
                                        <MenuItem value="tracking_number" dir={uiDirection}>{t("tracking_number")}</MenuItem>
                                        <MenuItem value="phone_number" dir={uiDirection}>{t("phone_number")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9} lg={10}>
                                <Grid container spacing="5px">
                                    <Grid item xs={4} display={searchBy !== 'phone_number' ? "none" : "block"}>
                                        {countrySelect}
                                    </Grid>
                                    <Grid item xs={searchBy !== 'phone_number' ? 12 : 8}>
                                        <CustomTextField label={t(INPUT_LABEL_KEYS[searchBy])} value={inputValue}
                                                         onChange={setInputValue} disabled={!isOrgSet || loading} type={searchBy} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Button fullWidth id="track-btn" variant="contained" type="submit" size="medium"
                                    style={buttonStyle} disabled={!canSubmitSearchStep}>
                                {t(needsPhoneVerification ? "continue_button" : "track_button")}
                                {loading ? <CircularProgress size={20} style={{ marginInlineStart: 10, color: theme.buttonTextColor }} /> : forwardIcon}
                            </Button>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Typography id="verify-phone-prompt" variant="body2" component="p"
                                    style={{ color: theme.primaryTextColor, margin: '0 0 2px', textAlign: 'start' }}>
                            {t("verify_phone_prompt")}
                        </Typography>
                        <Grid container spacing="5px">
                            <Grid item xs={4}>{countrySelect}</Grid>
                            <Grid item xs={8}>
                                <CustomTextField label={t("verify_phone_label")} value={verifyPhone}
                                                 onChange={setVerifyPhone} disabled={!isOrgSet || loading} type="verify_phone"
                                                 autoFocus inputProps={{ inputMode: 'tel' }} />
                            </Grid>
                        </Grid>
                        <Grid container columnSpacing="5px">
                            <Grid item xs={4}>
                                <Button fullWidth id="verify-back-btn" variant="outlined" size="medium" type="button"
                                        disabled={loading} onClick={() => setStep('search')}
                                        style={{ borderColor: theme.buttonBackgroundColor, color: theme.primaryTextColor, marginTop: 5 }}>
                                    {backIcon}{t("back_button")}
                                </Button>
                            </Grid>
                            <Grid item xs={8}>
                                <Button fullWidth id="track-btn" variant="contained" type="submit" size="medium"
                                        style={buttonStyle} disabled={!canSubmitVerifyStep}>
                                    {t("track_button")}
                                    {loading ? <CircularProgress size={20} style={{ marginInlineStart: 10, color: theme.buttonTextColor }} /> : forwardIcon}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </form>
        </ThemeProvider>
    );
};

export default TrackForm;